import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, GripVertical, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { reorderAfter } from '../scheduleParams.js';
import type { ScheduleCar } from '../types.js';

/**
 * 316 (FR-022) — car-row reordering, kept. A panel with its own drag list (pointer and keyboard via
 * `@dnd-kit/sortable`) and Move up / Move down buttons. Kept out of the timeline's rows on purpose:
 * a sortable handle there would share the timeline's drag context and collide with its row targets.
 * Each move reports 301's rule — the car and the car it now follows (null when first).
 */
export function CarReorderPanel({
  cars,
  pending,
  onMove,
  onClose,
}: {
  cars: ScheduleCar[];
  pending: boolean;
  onMove: (carId: string, afterCarId: string | null) => void;
  onClose: () => void;
}) {
  const ids = cars.map((c) => c.id);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const move = (from: number, to: number) => {
    const r = reorderAfter(ids, from, to);
    if (r) onMove(r.carId, r.afterCarId);
  };
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over) return;
    move(ids.indexOf(String(e.active.id)), ids.indexOf(String(e.over.id)));
  };
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 pt-20">
      <div
        ref={boxRef}
        // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
        role="dialog"
        aria-modal="true"
        aria-label="Reorder cars"
        className="flex max-h-[70vh] w-full max-w-sm flex-col rounded-xl border border-hairline bg-canvas p-3 shadow-xl"
      >
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-ink">Reorder cars</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-steel hover:text-ink"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <p className="mb-2 text-xs text-steel">
          Drag a car, or use the arrows. Each move is saved at once.
        </p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <ol className="grid gap-1 overflow-y-auto">
              {cars.map((car, i) => (
                <SortableCar
                  key={car.id}
                  car={car}
                  first={i === 0}
                  last={i === cars.length - 1}
                  disabled={pending}
                  onUp={() => move(i, i - 1)}
                  onDown={() => move(i, i + 1)}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
      </div>
    </div>,
    document.body,
  );
}

function SortableCar({
  car,
  first,
  last,
  disabled,
  onUp,
  onDown,
}: {
  car: ScheduleCar;
  first: boolean;
  last: boolean;
  disabled: boolean;
  onUp: () => void;
  onDown: () => void;
}) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: car.id,
    disabled,
  });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[
        'flex items-center gap-2 rounded border border-hairline bg-canvas px-2 py-1.5',
        isDragging ? 'opacity-60 shadow-lg' : '',
      ].join(' ')}
    >
      <button
        type="button"
        aria-label={`Drag ${car.label}`}
        className="cursor-grab touch-none text-steel"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>
      <span className="min-w-0 flex-1 truncate text-sm text-ink">
        {car.label} <span className="text-steel">· {car.plate_number}</span>
      </span>
      <button
        type="button"
        aria-label={`Move ${car.label} up`}
        disabled={first || disabled}
        onClick={onUp}
        className="rounded p-1 text-steel hover:text-ink disabled:opacity-30"
      >
        <ArrowUp size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label={`Move ${car.label} down`}
        disabled={last || disabled}
        onClick={onDown}
        className="rounded p-1 text-steel hover:text-ink disabled:opacity-30"
      >
        <ArrowDown size={14} aria-hidden="true" />
      </button>
    </li>
  );
}
